import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportCriteriaScores } from './api/criteriaScoreApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import InfoBanner from '../../components/ui/InfoBanner'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { criteriaScoreColumns } from './components/CriteriaScoreColumns'
import CriteriaScoreFormDialog from './components/CriteriaScoreFormDialog'
import { useCriteriaScores } from './hooks/useCriteriaScores'
import type { CriteriaScore, CriteriaScoreUpsert } from './types'
import ReorderableLookupCards from '../../components/ui/ReorderableLookupCards'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

/** Aktif kayıtların ağırlık toplamının hedefi (tablo yorumu: aktif toplam 100 olmalı). */
const WEIGHT_TARGET = 100

/**
 * Etkinlik Takip → Değerlendirme Kriterleri yönetim ekranı (028). Liste (paylaşılan ui/DataTable) + ekle/
 * düzenle (FormDialog) + durum toggle + soft-delete (confirmDialog) + tablo üzeri ↑/↓ sıralama. Başlık = menü
 * adı (`nav.etkinlik.criteriaScores`). **Ek:** aktif ağırlıkların canlı toplamı gösterilir; toplam 100 değilse
 * bloklamayan bir uyarı çıkar (kullanıcı kararı — kaydetme/silme engellenmez). (025 InvitationTypesPage deseni.)
 */
export default function CriteriaScoresPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, create, update, changeStatus, remove, reorder } = useCriteriaScores()
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CriteriaScore | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  // Aktif ağırlık toplamı (yalnız record_status=1). Bloklamayan uyarı için (toplam 100 olmalı).
  const activeWeightSum = rows
    .filter((r) => r.recordStatus === 1)
    .reduce((sum, r) => sum + r.weight, 0)
  const weightOk = activeWeightSum === WEIGHT_TARGET

  const submitting = create.isPending || update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: CriteriaScore) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('activity.criteriaScores.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: CriteriaScoreUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('activity.criteriaScores.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('activity.criteriaScores.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: CriteriaScore, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('activity.criteriaScores.toast.statusChanged')),
        // Hata (optimistic-lock/generic) → merkezî toast (019).
      },
    )
  }

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip tüm id sırasını backend'e yaz (1..N).
  const onMove = (row: CriteriaScore, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    reorder.mutate(orderedIds, {
      onError: () => notify('error', t('common.reorder.error')),
    })
  }

  const onDelete = (row: CriteriaScore) => {
    confirmDialog({
      header: t('activity.criteriaScores.delete.title'),
      message: t('activity.criteriaScores.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('activity.criteriaScores.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('activity.criteriaScores.toast.deleted')),
          // Hata → merkezî toast (019).
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <CriteriaScoreFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.etkinlik.criteriaScores')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportCriteriaScores} disabled={rows.length === 0} />
            <Button
              icon="pi pi-plus"
              label={t('activity.criteriaScores.actions.new')}
              onClick={openCreate}
            />
          </>
        }
      />

      {/* Aktif ağırlık toplamı: 100 ise bilgi (mavi), değilse bloklamayan uyarı (amber) — paylaşılan InfoBanner. */}
      {rows.length > 0 && (
        <InfoBanner
          tone={weightOk ? 'info' : 'warn'}
          icon={weightOk ? 'pi pi-info-circle' : 'pi pi-exclamation-triangle'}
          title={
            weightOk
              ? t('activity.criteriaScores.weightSum', { sum: activeWeightSum })
              : t('activity.criteriaScores.weightWarning', { sum: activeWeightSum })
          }
        />
      )}

      {/* Sıralanabilir (SORT_INDEX) liste → sayfalama kapalı, kolon sıralaması yok; daima sıra düzeninde. */}
      {isMobile ? (
        <ReorderableLookupCards
          items={rows}
          loading={list.isLoading}
          busyId={busyId}
          reordering={reorder.isPending}
          onMove={onMove}
          onEdit={openEdit}
          onToggle={onToggle}
          onDelete={onDelete}
          renderExtra={(row) => (
            <span className="ui-card-row">{`%${row.weight}`}</span>
          )}
          emptyTitle={t('activity.criteriaScores.empty.title')}
          emptyDescription={t('activity.criteriaScores.empty.desc')}
          labels={{
            active: t('activity.criteriaScores.status.active'),
            passive: t('activity.criteriaScores.status.passive'),
            edit: t('activity.criteriaScores.actions.edit'),
            delete: t('activity.criteriaScores.actions.delete'),
            toggle: t('activity.criteriaScores.actions.toggle'),
            moveUp: t('common.reorder.up'),
            moveDown: t('common.reorder.down'),
            menu: t('common.actionsMenu'),
          }}
        />
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        paginator={false}
        emptyMessage={
          <EmptyState
            title={t('activity.criteriaScores.empty.title')}
            description={t('activity.criteriaScores.empty.desc')}
          />
        }
      >
        {criteriaScoreColumns({
          t,
          localized,
          busyId,
          rowCount: rows.length,
          reordering: reorder.isPending,
          onMove,
          onEdit: openEdit,
          onToggle,
          onDelete,
        })}
      </DataTable>
      )}
    </div>
  )
}
